import indexOf from 'lodash-es/indexOf';
import sortBy from 'lodash-es/sortBy';
import { useEffect, useMemo } from 'react';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import {
  CareBenefitUnitOfMeasure,
  useCalculatePetBenefitUsageLazyQuery,
  CarePlanBenefitUsageFragment,
  CareBenefitType,
  CarePlanBenefitSourceFragment,
} from 'shared/types/graphql';

export type UsePetBenefitCareBenefit = {
  id: string;
  title: string;
  quantity: number;
  provided: number;
  tangible: boolean;
  exhaustible: boolean;
  exhausted: boolean;
  usageUnitOfMeasure?: CareBenefitUnitOfMeasure | null;
  discountPercentage?: number | null;
  type: CareBenefitType;
  planBenefits?: CarePlanBenefitUsageFragment[] | null;
  sources: CarePlanBenefitSourceFragment;
  displayOrder?: number | null;
  organizationPetId?: string | null;
  carryoverQuantity: number;
  carryoverProvided: number;
  used: number;
  costCovered: number;
};

interface IUsePetBenefitCareBenefitOutput {
  benefits: UsePetBenefitCareBenefit[];
  hasBenefitUsages: boolean;
  refresh: () => Promise<void>;
  loading: boolean;
}

const benefitsOrderByType = [
  CareBenefitType.AccountCredit,
  CareBenefitType.ProvidedService,
  CareBenefitType.Discount,
  CareBenefitType.Teletriage,
  CareBenefitType.ExternalAccess,
];

/**
 * Retrieve the benefits and usages of a particular organization pet
 * @param organizationPetId The organization pet id of the pet whose benefits we want to retrieve
 * @returns A list of benefits and the ability to ask for a refresh
 */
export const usePetBenefits = (organizationPetId?: string): IUsePetBenefitCareBenefitOutput => {
  const [getUsageData, { data: petBenefitData, loading: isLoadingPetBenefitData }] =
    useCalculatePetBenefitUsageLazyQuery({
      variables: {
        where: {
          organizationPetId: organizationPetId || '',
        },
      },
      fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    });

  useEffect(() => {
    if (organizationPetId) {
      getUsageData();
    }
  }, [organizationPetId, getUsageData]);
  const usages = useMemo(() => petBenefitData?.calculatePetBenefitUsage?.usages || [], [petBenefitData]);

  // Return a list of benefits sorted by benefitsOrderByType
  const benefits: UsePetBenefitCareBenefit[] = useMemo(
    () =>
      sortBy(
        usages.map((usage) => {
          const carryover = usage.carryover || 0;
          const provided = usage.provided - carryover;
          let quantity = 0;
          const carryoverQuantity = carryover - usage.used;

          if (carryover > usage.used) {
            quantity = usage.provided - carryover;
          } else {
            quantity = usage.provided - usage.used;
          }

          return {
            id: usage.benefit.id,
            title: usage.benefit.title,
            quantity,
            provided,
            carryoverQuantity,
            carryoverProvided: carryover,
            used: usage.used,
            costCovered: usage.costCovered || 0,
            tangible: usage.benefit.tangible,
            exhaustible: usage.benefit.exhaustible,
            exhausted: usage.exhausted,
            usageUnitOfMeasure: usage.benefit.usageUnitOfMeasure,
            discountPercentage: usage.benefit?.planBenefits?.find(
              (planBenefit) =>
                planBenefit?.providedDiscountPercentage &&
                planBenefit?.benefitId === usage?.benefit?.id &&
                planBenefit?.planId === usage?.sources.planIds[0],
            )?.providedDiscountPercentage,
            type: usage.benefit.type,
            planBenefits: usage.benefit?.planBenefits,
            sources: usage.sources,
            displayOrder: usage.displayOrder,
            organizationPetId: organizationPetId,
          };
        }),
        (usage) => indexOf(benefitsOrderByType, usage.type),
      ),
    [organizationPetId, usages],
  );

  const hasBenefitUsages = useMemo(() => benefits.some((benefit) => benefit.used > 0), [benefits]);

  return {
    benefits,
    hasBenefitUsages,
    refresh: async (): Promise<void> => {
      if (organizationPetId) {
        await getUsageData();
      }
    },
    loading: isLoadingPetBenefitData,
  };
};
